import type { Action } from '../App/types';
import type { LoginState } from './types';
import * as ActionTypes from './constants';
import { LOG_OUT } from '../App/constants';

const initialState: LoginState = {
  username: '',
  rememberMe: false,
  showErrorMessage: false,
  errorMessage: '',
  isSsoSubmitting: false,
  // Azure SSO state
  availableUsers: [],
  azureIdToken: null,
  showUserPicker: false,
};

const loginReducer = (
  state: LoginState = initialState,
  action: Action<*, *>,
): LoginState => {
  const { type, payload } = action;
  switch (type) {
    case ActionTypes.LOGIN_FAIL: {
      let errorMessage = 'Error Logging In';
      try {
        errorMessage = JSON.stringify(payload)
          .replace(/[^a-zA-Z0-9\s:,]/g, '')
          .trim();
      } catch (error) {
        console.error('Error parsing payload', error);
      }
      if (payload?.networkError === 'Failed to fetch') {
        errorMessage =
          'Unable to connect to Fortress. Please check your internet connection and try again. If the problem continues, contact your system administrator.'; // eslint-disable-line max-len
      }

      return {
        ...state,
        showErrorMessage: true,
        errorMessage,
      };
    }
    case ActionTypes.LOGIN: {
      return {
        ...state,
        showErrorMessage: false,
        errorMessage: '',
        username: payload.rememberMe ? payload.username : '',
        rememberMe: payload.rememberMe,
      };
    }
    case ActionTypes.FORGET_ME: {
      return {
        ...state,
        rememberMe: false,
        username: '',
      };
    }
    case ActionTypes.CLEAR_MESSAGES: {
      return {
        ...state,
        showErrorMessage: false,
        errorMessage: '',
      };
    }
    case ActionTypes.AZURE_SSO_LOGIN: {
      return {
        ...state,
        isSsoSubmitting: true,
        showErrorMessage: false,
        errorMessage: '',
      };
    }
    case ActionTypes.AZURE_SSO_LOGIN_SUCCESS:
    case ActionTypes.AZURE_SSO_LOGIN_FAIL: {
      return {
        ...state,
        isSsoSubmitting: false,
      };
    }
    case ActionTypes.AZURE_SSO_SHOW_USER_PICKER: {
      return {
        ...state,
        isSsoSubmitting: false,
        showUserPicker: true,
        availableUsers: payload.users,
        azureIdToken: payload.azureIdToken,
      };
    }
    case ActionTypes.AZURE_SSO_UPDATE_USER_LAST_ACCESS: {
      return {
        ...state,
        availableUsers: state.availableUsers.map((user) =>
          user.id === payload.userId
            ? { ...user, lastSystemAccess: new Date().toISOString() }
            : user,
        ),
      };
    }
    case ActionTypes.AZURE_SSO_HIDE_USER_PICKER: {
      return {
        ...state,
        showUserPicker: false,
      };
    }
    case ActionTypes.AZURE_SSO_SELECT_USER: {
      return {
        ...state,
        isSsoSubmitting: true,
      };
    }
    case LOG_OUT: {
      // Reset login state on logout, but preserve rememberMe settings
      return {
        ...initialState,
        username: state.rememberMe ? state.username : '',
        rememberMe: state.rememberMe,
      };
    }
    default:
      return state;
  }
};

export default loginReducer;
